import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable from '@repo/ui/data-table'
import { DeleteIcon, PenIcon, PlusIcon, RelationIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'

import { createTeachingHallComment } from '../../../api/ols/teaching-hall-comments/create-teaching-hall-comment'
import { deleteTeachingHallComment } from '../../../api/ols/teaching-hall-comments/delete-teaching-hall-comments'
import { getAllTeachingHallComments } from '../../../api/ols/teaching-hall-comments/get-teaching-hall-comment'
import { updateTeachingHallComment } from '../../../api/ols/teaching-hall-comments/update-teaching-hall-comment'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TeachingHallComment as TeachingHallCommentType } from '../../../lib/types/models/ol/teaching-hall-comment/teaching-hall-comment'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { TeachingHallCommentForm } from './teaching-hall-comment.form'

type Props = {
  teachingHallId: number
}

export function TeachingHallComment({ teachingHallId }: Props) {
  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'create' | 'edit' | 'delete',
    TeachingHallCommentType
  >(['create', 'edit', 'delete'])

  const commentsQuery = useQuery({
    queryKey: ['teaching-hall-comments', teachingHallId],
    queryFn: () => getAllTeachingHallComments({ teachingHallId }),
    enabled: !!teachingHallId
  })

  const createCommentMutation = useMutation({
    mutationFn: createTeachingHallComment,
    onSuccess: async () => {
      await commentsQuery.refetch()
      closeModel('create')
    },
    onError: showAxiosError
  })

  const updateCommentMutation = useMutation({
    mutationFn: updateTeachingHallComment,
    onSuccess: async () => {
      await commentsQuery.refetch()
      closeModel('edit')
    },
    onError: showAxiosError
  })

  const deleteCommentMutation = useMutation({
    mutationFn: deleteTeachingHallComment,
    onSuccess: async () => {
      await commentsQuery.refetch()
      closeModel('delete')
    },
    onError: showAxiosError
  })

  return (
    <div>
      <InnerBox className="rounded-xl gap-3">
        <div className="flex flex-row gap-3 items-center">
          <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
            LO comments
          </Typography>

          <Button
            label="Add comment"
            icon={PlusIcon}
            variant="outline"
            onClick={() => toggleModal('create')}
          />
        </div>

        <DataTable
          columns={[
            {
              accessorKey: 'comment',
              header: 'Comments',
              enableSorting: true,
              sortingFn: 'text'
            },
            {
              accessorKey: 'createdAt',
              header: 'Date & time',
              enableSorting: true,
              cell: ({ row }) => (
                <Badge text={DateFormatter.ddMMYYYY_HHMM(row.original.createdAt)} />
              )
            },
            {
              accessorKey: 'id',
              header: '',
              cell: ({ row }) => {
                return (
                  <div className="flex flex-row gap-2 items-center">
                    <ActionButton
                      variant="primary"
                      icon={PenIcon}
                      onClick={() => toggleModal('edit', row.original)}
                    />
                    <ActionButton
                      variant="danger"
                      icon={DeleteIcon}
                      onClick={() => toggleModal('delete', row.original)}
                    />
                  </div>
                )
              }
            }
          ]}
          data={commentsQuery.data?.data ?? []}
          paginated={false}
          rowCount={0}
          manualSorting={false}
        />
      </InnerBox>

      <Modal
        isOpen={modalState.create}
        title={'Add a comment'}
        icon={() => <RelationIcon color={tailwindColors.secondary.DEFAULT} />}
        size="small"
        hideActions
        onCancel={() => closeModel('create')}
      >
        <TeachingHallCommentForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            isLoading: createCommentMutation.isPending,
            isEnglish: true,
            onCancel: () => closeModel('create'),
            onConfirm: (value) => createCommentMutation.mutate({ teachingHallId, ...value })
          })}
        />
      </Modal>

      {selectedData && (
        <>
          <Modal
            isOpen={modalState.edit}
            title={'Edit a comment'}
            icon={() => <RelationIcon color={tailwindColors.secondary.DEFAULT} />}
            size="small"
            hideActions
            onCancel={() => closeModel('edit')}
          >
            <TeachingHallCommentForm
              className={MODAL_BODY_CLASSNAME}
              defaultValues={{ comment: selectedData.comment }}
              buttonsConfig={modalButtonConfig({
                isLoading: updateCommentMutation.isPending,
                isEnglish: true,
                onCancel: () => closeModel('edit'),
                onConfirm: (value) =>
                  updateCommentMutation.mutate({ teachingHallId, ...value, id: selectedData.id })
              })}
            />
          </Modal>

          <Modal
            isOpen={modalState.delete}
            title={'Delete a comment'}
            icon={DeleteIcon}
            size="small"
            variant="destructive"
            onCancel={() => closeModel('delete')}
            isLoading={deleteCommentMutation.isPending}
            actionButtonsOptions={{
              cancelText: 'Cancel',
              confirmText: 'Delete',

              onConfirm: () =>
                deleteCommentMutation.mutate({ teachingHallId, ids: [selectedData.id] })
            }}
          />
        </>
      )}
    </div>
  )
}
